import { useAuthorizedRequest } from '@/hooks/useRequest';
import { apiRequest } from '@/lib/api';
import type { Client } from '@/lib/Client';
import type { Lesson, LessonAttributes } from '@/lib/Lesson';
import type { Subject } from '@/lib/Subject';
import { getSubjectPrettyName } from '@/lib/Subject';
import type { Teacher } from '@/lib/Teacher';
import type { Student } from '@/lib/Student';
import { t } from '@/lib/i18n';
import { Badge, Box, Button, CloseButton, Collapsible, Drawer, Grid, HStack, Input, Portal, Skeleton, Table, Text, Tooltip, VStack } from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { mutate as globalMutate } from 'swr';

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
}

const selectStyle = {
    width: '100%', padding: '4px 8px', borderRadius: '4px',
    border: '1px solid #ccc', fontSize: '14px', background: 'transparent',
};

function nowLocal(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function LessonEditDrawer({ lesson, teachers, subjects, open, onClose, onSave }: {
    lesson: Lesson; teachers: Teacher[]; subjects: Subject[]; open: boolean; onClose: () => void;
    onSave: (attrs: LessonAttributes) => Promise<void>;
}) {
    const activeTeachers = teachers.filter(t => !t.deleted);
    const activeSubjects = subjects.filter(s => !s.deleted);
    const [teacherId, setTeacherId] = useState(lesson.teacher_id);
    const [dateTime, setDateTime] = useState(lesson.date_time_local.slice(0, 16).replace(' ', 'T'));
    const [priceUah, setPriceUah] = useState(String(lesson.price_uah));
    const [durationMin, setDurationMin] = useState(String(lesson.duration_minutes));
    const [subject, setSubject] = useState(lesson.subject ?? '');
    const [individual, setIndividual] = useState(lesson.individual);
    const [online, setOnline] = useState(lesson.online);
    const [completed, setCompleted] = useState(lesson.completed);
    const [comment, setComment] = useState(lesson.comment ?? '');
    const [deleted, setDeleted] = useState(lesson.deleted);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit() {
        const price = parseInt(priceUah, 10);
        const dur = parseInt(durationMin, 10);
        if (isNaN(price) || price < 0) { setError(t('lessons.price_invalid')); return; }
        if (isNaN(dur) || dur <= 0) { setError(t('lessons.duration_invalid')); return; }
        setSubmitting(true); setError('');
        try {
            const dtLocal = dateTime.replace('T', ' ') + ':00';
            await onSave({
                student_id: lesson.student_id,
                teacher_id: teacherId,
                date_time_local: dtLocal,
                price_uah: price,
                duration_minutes: dur,
                subject: (subject || undefined),
                individual,
                online,
                completed,
                comment: comment.trim() || undefined,
                deleted,
            });
            onClose();
        } catch (e) { setError(e instanceof Error ? e.message : t('error.unknown')); }
        finally { setSubmitting(false); }
    }

    return (
        <Drawer.Root open={open} onOpenChange={e => { if (!e.open) onClose(); }} placement="end">
            <Portal><Drawer.Backdrop /><Drawer.Positioner><Drawer.Content>
                <Drawer.Header><Drawer.Title>{t('lessons.edit_title')}</Drawer.Title></Drawer.Header>
                <Drawer.Body><VStack gap={3} align="stretch">
                    {error && <Text color="red.500" fontSize="sm">{error}</Text>}
                    <Box><Text fontSize="sm" mb="1">{t('lessons.student_readonly')}</Text><Text fontSize="xs" color="gray.500">{lesson.student_id}</Text></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.teacher')} *</Text>
                        <select value={teacherId} onChange={e => setTeacherId(e.target.value)} style={selectStyle}>
                            {activeTeachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.date')} *</Text><Input size="sm" type="datetime-local" value={dateTime} onChange={e => setDateTime(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.price')} (₴)</Text><Input size="sm" type="number" min="0" value={priceUah} onChange={e => setPriceUah(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.duration')}</Text><Input size="sm" type="number" min="1" value={durationMin} onChange={e => setDurationMin(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.subject')}</Text>
                        <select value={subject} onChange={e => setSubject(e.target.value)} style={selectStyle}>
                            <option value="">{t('subject.none')}</option>
                            {activeSubjects.map(s => <option key={s.id} value={s.name}>{getSubjectPrettyName(s.name)}</option>)}
                        </select>
                    </Box>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={individual} onChange={e => setIndividual(e.target.checked)} /> {t('label.individual')}</label>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={online} onChange={e => setOnline(e.target.checked)} /> {t('label.online')}</label>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={completed} onChange={e => setCompleted(e.target.checked)} /> {t('label.completed')}</label>
                    <Box><Text fontSize="sm" mb="1">{t('label.comment')}</Text><Input size="sm" value={comment} onChange={e => setComment(e.target.value)} /></Box>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={deleted} onChange={e => setDeleted(e.target.checked)} /> {t('label.deleted')}</label>
                </VStack></Drawer.Body>
                <Drawer.Footer><HStack><Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button><Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button></HStack></Drawer.Footer>
                <Drawer.CloseTrigger asChild><CloseButton size="xs" variant="solid" /></Drawer.CloseTrigger>
            </Drawer.Content></Drawer.Positioner></Portal>
        </Drawer.Root>
    );
}

export function Lessons() {
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchText, setSearchText] = useState('');
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const [editLesson, setEditLesson] = useState<Lesson | null>(null);

    // --- Filter param helpers ---
    const getFilter = useCallback((key: string): string => {
        return searchParams.get(key) ?? '';
    }, [searchParams]);

    const setFilter = useCallback((key: string, value: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (value) next.set(key, value);
            else next.delete(key);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const clearFilters = useCallback(() => {
        setSearchParams({}, { replace: true });
    }, [setSearchParams]);

    const activeFilterCount = useMemo(() => {
        const filterKeys = ['date_from', 'date_to', 'client_id', 'teacher_id', 'student_id', 'subject', 'duration_min', 'duration_max', 'price_min', 'price_max', 'completed'];
        return filterKeys.filter(k => searchParams.has(k)).length;
    }, [searchParams]);

    // Build API URL from search params
    const apiKey = useMemo(() => {
        const params = searchParams.toString();
        return params ? `/lessons?${params}` : '/lessons';
    }, [searchParams]);

    // --- Data fetching ---
    const { data: lessonsData, error, isLoading, isAuthenticated, mutate } =
        useAuthorizedRequest<{ lessons: Lesson[] }>(apiKey, jsonFetcher);
    const { data: teachersData } =
        useAuthorizedRequest<{ teachers: Teacher[] }>('/teachers', jsonFetcher);
    const { data: clientsData } =
        useAuthorizedRequest<{ clients: Client[] }>('/clients', jsonFetcher);
    const { data: subjectsData } =
        useAuthorizedRequest<{ subjects: Subject[] }>('/subjects', jsonFetcher);
    const { data: studentsData } =
        useAuthorizedRequest<{ students: Student[] }>('/students', jsonFetcher);

    // --- Lookup maps ---
    const clientMap = useMemo(() => {
        const map: Record<string, Client> = {};
        for (const c of clientsData?.clients ?? []) map[c.id] = c;
        return map;
    }, [clientsData]);

    const teacherMap = useMemo(() => {
        const map: Record<string, Teacher> = {};
        for (const t of teachersData?.teachers ?? []) map[t.id] = t;
        return map;
    }, [teachersData]);

    const studentMap = useMemo(() => {
        const map: Record<string, Student> = {};
        for (const s of studentsData?.students ?? []) {
            const compositeId = `${s.client_id}:${s.id}`;
            map[compositeId] = s;
        }
        return map;
    }, [studentsData]);

    // --- Derived lists ---
    const activeTeachers = useMemo(() => (teachersData?.teachers ?? []).filter(t => !t.deleted), [teachersData]);
    const activeClients = useMemo(() => (clientsData?.clients ?? []).filter(c => !c.deleted), [clientsData]);
    const activeSubjects = useMemo(() => (subjectsData?.subjects ?? []).filter(s => !s.deleted), [subjectsData]);
    const activeStudents = useMemo(() => (studentsData?.students ?? []).filter(s => !s.deleted), [studentsData]);

    const teachers = useMemo(() => teachersData?.teachers ?? [], [teachersData]);
    const subjects = useMemo(() => subjectsData?.subjects ?? [], [subjectsData]);

    // Filter active students by selected client if client filter is active
    const filteredStudentsForPicker = useMemo(() => {
        const clientId = getFilter('client_id');
        if (!clientId) return activeStudents;
        return activeStudents.filter(s => s.client_id === clientId);
    }, [activeStudents, getFilter]);

    // --- Client-side text filtering + sorting (on top of server-side filtered data) ---
    const lessons = useMemo(() => {
        return (lessonsData?.lessons ?? [])
            .filter(l => !l.deleted)
            .filter(l => {
                if (!searchText) return true;
                const idx = l.student_id.indexOf(':');
                const clientId = idx > 0 ? l.student_id.substring(0, idx) : l.student_id;
                const client = clientMap[clientId];
                const teacher = teacherMap[l.teacher_id];
                return [l.student_id, l.teacher_id, client?.name, teacher?.name, l.subject, l.date_time_local]
                    .some(s => s?.toLowerCase().includes(searchText.toLowerCase()));
            })
            .sort((a, b) => b.date_time_local.localeCompare(a.date_time_local));
    }, [lessonsData, clientMap, teacherMap, searchText]);

    // --- Action handlers ---
    async function handleEditLesson(attrs: LessonAttributes) {
        if (!editLesson) return;
        const token = await getAccessTokenSilently();
        const teacherDatetime = `${editLesson.teacher_id}_${editLesson.date_time_local}`;
        await apiRequest('PUT', `/lessons/${editLesson.student_id}/${teacherDatetime}`, token, attrs);
        await mutate();
        globalMutate(
            (key: unknown) => typeof key === 'string' && key.includes('/reports/most-indebted'),
            undefined,
            { revalidate: true }
        );
        setEditLesson(null);
    }

    async function handleToggleComplete(l: Lesson) {
        const token = await getAccessTokenSilently();
        const teacherDatetime = `${l.teacher_id}_${l.date_time_local}`;
        const newCompleted = !l.completed;
        await apiRequest('PUT', `/lessons/${l.student_id}/${teacherDatetime}`, token, {
            student_id: l.student_id,
            teacher_id: l.teacher_id,
            date_time_local: l.date_time_local,
            price_uah: l.price_uah,
            duration_minutes: l.duration_minutes,
            subject: l.subject || undefined,
            individual: l.individual,
            online: l.online,
            completed: newCompleted,
            comment: l.comment || undefined,
            deleted: l.deleted,
        });
        await mutate();
        globalMutate(
            (key: unknown) => typeof key === 'string' && key.includes('/reports/most-indebted'),
            undefined,
            { revalidate: true }
        );
    }

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (error) return <div>{t('label.error')}: {(error as Error).message}</div>;

    return (
        <VStack gap={4} align="stretch">
            {/* Free-text search — always visible */}
            <HStack>
                <Input
                    placeholder={t('lessons.search')}
                    variant="subtle"
                    flex="1"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                />
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setFilterPanelOpen(!filterPanelOpen)}
                >
                    {t('lessons.filter_toggle')}
                    {activeFilterCount > 0 && (
                        <Badge ml={1} colorPalette="blue">{activeFilterCount}</Badge>
                    )}
                </Button>
            </HStack>

            {/* Collapsible filter panel */}
            <Collapsible.Root open={filterPanelOpen} onOpenChange={e => setFilterPanelOpen(e.open)}>
                <Collapsible.Content>
                    <Box borderWidth="1px" borderRadius="md" p={4} bg="bg.subtle">
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                            {/* Date range */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_date_from')}</Text>
                                <Input type="date" size="sm" value={getFilter('date_from')} onChange={e => setFilter('date_from', e.target.value)} />
                            </Box>
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_date_to')}</Text>
                                <Input type="date" size="sm" value={getFilter('date_to')} onChange={e => setFilter('date_to', e.target.value)} />
                            </Box>

                            {/* Client picker */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_client')}</Text>
                                <select
                                    value={getFilter('client_id')}
                                    onChange={e => setFilter('client_id', e.target.value)}
                                    style={selectStyle}
                                >
                                    <option value="">{t('lessons.filter_client_placeholder')}</option>
                                    {activeClients.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                                {activeClients.length === 0 && (
                                    <Text fontSize="xs" color="gray.500">{t('lessons.filter_no_clients')}</Text>
                                )}
                            </Box>

                            {/* Teacher picker */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_teacher')}</Text>
                                <select
                                    value={getFilter('teacher_id')}
                                    onChange={e => setFilter('teacher_id', e.target.value)}
                                    style={selectStyle}
                                >
                                    <option value="">{t('lessons.filter_teacher_placeholder')}</option>
                                    {activeTeachers.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                                {activeTeachers.length === 0 && (
                                    <Text fontSize="xs" color="gray.500">{t('lessons.filter_no_teachers')}</Text>
                                )}
                            </Box>

                            {/* Student picker */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_student')}</Text>
                                <select
                                    value={getFilter('student_id')}
                                    onChange={e => setFilter('student_id', e.target.value)}
                                    style={selectStyle}
                                >
                                    <option value="">{t('lessons.filter_student_placeholder')}</option>
                                    {filteredStudentsForPicker.map(s => {
                                        const client = clientMap[s.client_id];
                                        const label = client ? `${client.name} → ${s.name}` : s.name;
                                        const compositeId = `${s.client_id}:${s.id}`;
                                        return (
                                            <option key={compositeId} value={compositeId}>{label}</option>
                                        );
                                    })}
                                </select>
                                {filteredStudentsForPicker.length === 0 && (
                                    <Text fontSize="xs" color="gray.500">{t('lessons.filter_no_students')}</Text>
                                )}
                            </Box>

                            {/* Subject picker */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_subject')}</Text>
                                <select
                                    value={getFilter('subject')}
                                    onChange={e => setFilter('subject', e.target.value)}
                                    style={selectStyle}
                                >
                                    <option value="">{t('lessons.filter_subject_placeholder')}</option>
                                    {activeSubjects.map(s => (
                                        <option key={s.id} value={s.name}>{getSubjectPrettyName(s.name)}</option>
                                    ))}
                                </select>
                            </Box>

                            {/* Duration range */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_duration_min')}</Text>
                                <Input type="number" size="sm" min="0" placeholder="0"
                                    value={getFilter('duration_min')}
                                    onChange={e => setFilter('duration_min', e.target.value)} />
                            </Box>
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_duration_max')}</Text>
                                <Input type="number" size="sm" min="0" placeholder="999"
                                    value={getFilter('duration_max')}
                                    onChange={e => setFilter('duration_max', e.target.value)} />
                            </Box>

                            {/* Price range */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_price_min')}</Text>
                                <Input type="number" size="sm" min="0" placeholder="0"
                                    value={getFilter('price_min')}
                                    onChange={e => setFilter('price_min', e.target.value)} />
                            </Box>
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_price_max')}</Text>
                                <Input type="number" size="sm" min="0" placeholder="99999"
                                    value={getFilter('price_max')}
                                    onChange={e => setFilter('price_max', e.target.value)} />
                            </Box>

                            {/* Status picker */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('lessons.filter_status')}</Text>
                                <select
                                    value={getFilter('completed')}
                                    onChange={e => setFilter('completed', e.target.value)}
                                    style={selectStyle}
                                >
                                    <option value="">{t('lessons.filter_status_all')}</option>
                                    <option value="true">{t('lessons.filter_status_completed')}</option>
                                    <option value="false">{t('lessons.filter_status_pending')}</option>
                                </select>
                            </Box>
                        </Grid>

                        {/* Clear filters button */}
                        {activeFilterCount > 0 && (
                            <Button size="sm" variant="ghost" mt={4} onClick={clearFilters}>
                                {t('lessons.filter_clear')}
                            </Button>
                        )}
                    </Box>
                </Collapsible.Content>
            </Collapsible.Root>

            {/* Lessons table */}
            <Box overflowX="auto">
                <Table.Root variant="line" showColumnBorder interactive size="sm">
                    <Table.Header>
                        <Table.Row>
                            <Table.ColumnHeader>{t('label.date')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.client')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.teacher')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.subject')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.duration')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.price')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.status')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.actions')}</Table.ColumnHeader>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {isLoading ? (
                            [...Array(8)].map((_, i) => (
                                <Table.Row key={i}>
                                    {[...Array(8)].map((_, j) => (
                                        <Table.Cell key={j}><Skeleton height="20px" /></Table.Cell>
                                    ))}
                                </Table.Row>
                            ))
                        ) : lessons.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={8} style={{ textAlign: 'center' }}>
                                    <Text color="gray.500">{t('label.no_data')}</Text>
                                </Table.Cell>
                            </Table.Row>
                        ) : lessons.map(l => {
                            const sepIdx = l.student_id.indexOf(':');
                            const clientId = sepIdx > 0 ? l.student_id.substring(0, sepIdx) : l.student_id;
                            const client = clientMap[clientId];
                            const teacher = teacherMap[l.teacher_id];
                            return (
                                <Table.Row key={`${l.student_id}:${l.teacher_id}:${l.date_time_local}`}>
                                    <Table.Cell>{l.date_time_local}</Table.Cell>
                                    <Table.Cell>
                                        {client ? (
                                            <Link to={`/client/${clientId}`} style={{ textDecoration: 'underline' }}>{client?.name ?? clientId}</Link>
                                        ) : <Text fontSize="xs">{clientId.slice(0, 8)}…</Text>}
                                    </Table.Cell>
                                    <Table.Cell>{teacher?.name ?? <Text fontSize="xs">{l.teacher_id.slice(0, 8)}…</Text>}</Table.Cell>
                                    <Table.Cell>{getSubjectPrettyName(l.subject)}</Table.Cell>
                                    <Table.Cell>{l.duration_minutes} min</Table.Cell>
                                    <Table.Cell>{l.price_uah} ₴</Table.Cell>
                                    <Table.Cell>
                                        <Button size="xs" variant={l.completed ? "solid" : "outline"} colorScheme={l.completed ? "green" : "gray"} onClick={() => handleToggleComplete(l)}>
                                            {l.completed ? t('lessons.done') : '○ ' + t('lessons.pending')}
                                        </Button>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Button size="xs" variant="ghost" onClick={() => setEditLesson(l)} aria-label={t('action.edit')}>✏️</Button>
                                            </Tooltip.Trigger>
                                            <Tooltip.Positioner>
                                                <Tooltip.Content>{t('action.edit')}</Tooltip.Content>
                                            </Tooltip.Positioner>
                                        </Tooltip.Root>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table.Root>
            </Box>

            {editLesson && teachers.length > 0 && (
                <LessonEditDrawer
                    lesson={editLesson}
                    teachers={teachers}
                    subjects={subjects}
                    open={!!editLesson}
                    onClose={() => setEditLesson(null)}
                    onSave={handleEditLesson}
                />
            )}
        </VStack>
    );
}
